/* ============================================================
   REGINA – FAQ-Seite
   assets/section-faq-page.js
   ------------------------------------------------------------
   <regina-faq>: Honig-Akkordeon, Themen-Navigation mit wandernder
   Markierung (zwei Federn: vordere Kante zieht, hintere folgt zäh),
   ruhiges Scrollen mit Honig-Kurve, Direktlinks, Link kopieren.
   Die Browsersuche (Strg+F) öffnet passende Antworten automatisch.
   ============================================================ */
(() => {
  if (customElements.get('regina-faq')) return;

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const UNTIL_FOUND = 'onbeforematch' in document.documentElement;

  // cubic-bezier(.7,0,.12,1) – dieselbe Honig-Kurve wie in den Stories
  function bezier(x1, y1, x2, y2) {
    const ax = 3 * x1 - 3 * x2 + 1, bx = 3 * x2 - 6 * x1, cx = 3 * x1;
    const ay = 3 * y1 - 3 * y2 + 1, by = 3 * y2 - 6 * y1, cy = 3 * y1;
    const X = (t) => ((ax * t + bx) * t + cx) * t, Y = (t) => ((ay * t + by) * t + cy) * t;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let lo = 0, hi = 1, t = x;
      for (let i = 0; i < 20; i++) { const v = X(t); if (Math.abs(v - x) < 1e-4) break; if (v < x) lo = t; else hi = t; t = (lo + hi) / 2; }
      return Y(t);
    };
  }
  const honey = bezier(0.62, 0, 0.12, 1);

  /* ---------- Feder ---------- */
  class Spring {
    constructor(v) { this.v = v; this.t = v; this.vel = 0; this.k = 300; this.c = 30; }
    step(dt) {
      const a = this.k * (this.t - this.v) - this.c * this.vel;
      this.vel += a * dt;
      this.v += this.vel * dt;
      return Math.abs(this.t - this.v) > 0.05 || Math.abs(this.vel) > 0.05;
    }
    snap(v) { this.v = this.t = v; this.vel = 0; }
  }

  /* ---------- Markierung mit zwei Kanten („Tropfen“) ---------- */
  class Indicator {
    constructor(el, axis) {
      this.el = el; this.axis = axis;
      this.a = new Spring(0); this.b = new Spring(0);
      this.raf = 0; this.ready = false;
    }
    to(start, end, instant) {
      if (!this.el) return;
      if (!this.ready || instant || REDUCED.matches) {
        this.a.snap(start); this.b.snap(end); this.ready = true; this.paint();
        this.el.classList.add('is-on');
        return;
      }
      const forward = start > this.a.t;
      // vordere Kante zieht straff, hintere folgt zäh und wippt nach
      const lead = { k: 380, c: 34 }, trail = { k: 150, c: 16 };
      Object.assign(forward ? this.b : this.a, lead);
      Object.assign(forward ? this.a : this.b, trail);
      this.a.t = start; this.b.t = end;
      if (!this.raf) { this.last = 0; this.raf = requestAnimationFrame((t) => this.loop(t)); }
    }
    loop(now) {
      const dt = this.last ? Math.min(1 / 30, (now - this.last) / 1000) : 1 / 60;
      this.last = now;
      const m1 = this.a.step(dt), m2 = this.b.step(dt);
      this.paint();
      this.raf = m1 || m2 ? requestAnimationFrame((t) => this.loop(t)) : 0;
      if (!this.raf) { this.a.snap(this.a.t); this.b.snap(this.b.t); this.paint(); }
    }
    paint() {
      const s = Math.min(this.a.v, this.b.v), size = Math.max(2, Math.abs(this.b.v - this.a.v));
      if (this.axis === 'y') { this.el.style.transform = `translateY(${s.toFixed(2)}px)`; this.el.style.height = `${size.toFixed(2)}px`; }
      else { this.el.style.transform = `translateX(${s.toFixed(2)}px)`; this.el.style.width = `${size.toFixed(2)}px`; }
    }
  }

  class ReginaFaq extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.fq-item')];
      this.sections = [...this.querySelectorAll('[data-fq-section]')];
      this.sideLinks = [...this.querySelectorAll('.fq-side__link')];
      this.chipLinks = [...this.querySelectorAll('.fq-chip')];
      this.chips = this.querySelector('.fq-chips');
      this.scroller = this.querySelector('.fq-chips__scroller');
      this.toast = this.querySelector('.fq-toast');
      this.indY = new Indicator(this.querySelector('.fq-ind--y'), 'y');
      this.indX = new Indicator(this.querySelector('.fq-ind--x'), 'x');
      this.active = null;

      this.items.forEach((item) => this.setupItem(item));
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => this.onKey(e));

      this.header = document.querySelector('.site-header');
      this.syncHeader();
      if (this.header) {
        this.mo = new MutationObserver(() => this.syncHeader());
        this.mo.observe(this.header, { attributes: true, attributeFilter: ['class'] });
      }

      this.wm = this.querySelector('.fq-wm');
      this.hero = this.querySelector('.fq-hero');
      this._onScroll = () => {
        if (!this.spyRaf) this.spyRaf = requestAnimationFrame(() => { this.spyRaf = 0; this.spy(); this.parallax(); });
      };
      this._onResize = () => { this.syncHeader(); this.placeIndicators(true); this.spy(); };
      this._onHash = () => this.fromHash(false);
      addEventListener('scroll', this._onScroll, { passive: true });
      addEventListener('resize', this._onResize, { passive: true });
      addEventListener('hashchange', this._onHash);

      // Theme-Editor: ausgewählte Frage öffnen
      this._onSel = (e) => {
        if (!this.contains(e.target)) return;
        const item = e.target.closest('.fq-item');
        if (item) { this.open(item, true); this.scrollToEl(item, true); return; }
        const sec = e.target.closest('[data-fq-section]');
        if (sec) this.goTopic(sec.dataset.fqSection, true);
      };
      document.addEventListener('shopify:block:select', this._onSel);

      requestAnimationFrame(() => {
        this.spy(true);
        setTimeout(() => this.fromHash(true), 80);
      });
      // Der Browser springt nach dem vollständigen Laden selbst noch einmal
      // zum Anker – danach unsere Position (unter Header/Leiste) wiederherstellen
      if (location.hash && document.readyState !== 'complete') {
        addEventListener('load', () => requestAnimationFrame(() => {
          const id = decodeURIComponent(location.hash.slice(1)), t = id && document.getElementById(id);
          if (t && this.contains(t)) this.scrollToEl(t, true, t.classList.contains('fq-item') ? 18 : 24);
        }), { once: true });
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.placeIndicators(true));
    }

    disconnectedCallback() {
      removeEventListener('scroll', this._onScroll);
      removeEventListener('resize', this._onResize);
      removeEventListener('hashchange', this._onHash);
      document.removeEventListener('shopify:block:select', this._onSel);
      this.mo && this.mo.disconnect();
      this.cancelScroll();
    }

    /* ============================================================
       Akkordeon
       ============================================================ */
    setupItem(item) {
      const panel = item.querySelector('.fq-panel');
      item._panel = panel;
      item._btn = item.querySelector('.fq-trigger');
      // Browsersuche (Strg+F) darf zugeklappte Antworten finden und öffnen
      if (UNTIL_FOUND) {
        panel.setAttribute('hidden', 'until-found');
        panel.addEventListener('beforematch', () => this.open(item, true));
      }
    }

    toggle(item) { item.classList.contains('is-open') ? this.close(item) : this.open(item); }

    open(item, instant) {
      if (item.classList.contains('is-open')) return;
      const panel = item._panel;
      clearTimeout(item._closeT);
      panel.removeAttribute('hidden');
      if (instant || REDUCED.matches) {
        panel.style.transition = 'none';
        item.classList.add('is-open');
        void panel.offsetHeight;
        panel.style.transition = '';
      } else {
        void panel.offsetHeight; // Startzustand 0fr festhalten
        item.classList.add('is-open');
      }
      item._btn.setAttribute('aria-expanded', 'true');
    }

    close(item) {
      if (!item.classList.contains('is-open')) return;
      const panel = item._panel;
      item.classList.remove('is-open');
      item._btn.setAttribute('aria-expanded', 'false');
      const done = () => {
        if (item.classList.contains('is-open')) return;
        panel.setAttribute('hidden', UNTIL_FOUND ? 'until-found' : '');
      };
      clearTimeout(item._closeT);
      item._closeT = setTimeout(done, REDUCED.matches ? 0 : 760);
    }

    /* ============================================================
       Kopf-Abstand: Header fährt ein/aus → Leisten rutschen mit
       ============================================================ */
    syncHeader() {
      const h = this.header;
      let top = 0;
      if (h) {
        const pos = getComputedStyle(h).position;
        const floating = pos === 'fixed' || pos === 'sticky';
        if (floating && !h.classList.contains('header-hidden')) top = h.offsetHeight;
      }
      this.top = top;
      this.style.setProperty('--fq-top', `${top}px`);
    }
    chipsH() { return this.chips && this.chips.offsetParent !== null ? this.chips.offsetHeight : 0; }
    headerH() { return this.header ? this.header.offsetHeight : 0; }

    // Wasserzeichen wandert beim Scrollen langsamer mit (Tiefe)
    parallax() {
      if (!this.wm || REDUCED.matches) return;
      const h = this.hero.offsetHeight;
      const y = Math.min(scrollY, h + 200);
      if (y > h + 200 && this._wmDone) return;
      this._wmDone = y > h + 200;
      this.wm.style.setProperty('--wm-y', `${(y * 0.14).toFixed(1)}px`);
    }

    /* ============================================================
       Scroll-Spy + Markierung
       ============================================================ */
    spy(instant) {
      if (this.lockSpy || !this.sections.length) return;
      const line = this.top + this.chipsH() + innerHeight * 0.22;
      let cur = this.sections[0];
      for (const s of this.sections) if (s.getBoundingClientRect().top <= line) cur = s;
      const atEnd = innerHeight + scrollY >= document.documentElement.scrollHeight - 4;
      if (atEnd) cur = this.sections[this.sections.length - 1];
      this.setActive(cur.dataset.fqSection, instant);
    }

    setActive(id, instant) {
      if (id === this.active && !instant) return;
      this.active = id;
      this.sections.forEach((s) => s.classList.toggle('is-current', s.dataset.fqSection === id));
      [...this.sideLinks, ...this.chipLinks].forEach((a) => {
        const on = a.dataset.fqTopic === id;
        a.classList.toggle('is-active', on);
        if (on) a.setAttribute('aria-current', 'location'); else a.removeAttribute('aria-current');
      });
      this.placeIndicators(instant);
      const chip = this.chipLinks.find((a) => a.dataset.fqTopic === id);
      if (chip && this.scroller && this.chips.offsetParent !== null) {
        const want = chip.offsetLeft - (this.scroller.clientWidth - chip.offsetWidth) / 2;
        this.scroller.scrollTo({ left: Math.max(0, want), behavior: instant || REDUCED.matches ? 'auto' : 'smooth' });
      }
    }

    placeIndicators(instant) {
      const side = this.sideLinks.find((a) => a.classList.contains('is-active'));
      if (side && side.offsetParent) {
        // offsetTop bezieht sich auf die (sticky) Navigation
        const top = side.offsetTop + 7, h = side.offsetHeight - 14;
        this.indY.to(top, top + h, instant);
      }
      const chip = this.chipLinks.find((a) => a.classList.contains('is-active'));
      if (chip && chip.offsetParent) {
        const left = chip.offsetLeft;
        this.indX.to(left, left + chip.offsetWidth, instant);
      }
    }

    /* ============================================================
       Scrollen mit Honig-Kurve (abbrechbar durch Nutzer)
       ============================================================ */
    scrollToY(y, instant, done) {
      this.cancelScroll();
      const max = document.documentElement.scrollHeight - innerHeight;
      y = clamp(y, 0, max);
      const from = scrollY, dist = y - from;
      // 'instant' übergeht die globale CSS-Regel html{scroll-behavior:smooth}
      if (instant || REDUCED.matches || Math.abs(dist) < 4) { scrollTo({ top: y, behavior: 'instant' }); done && done(); return; }
      const D = clamp(520 + Math.abs(dist) * 0.32, 650, 1350);
      const t0 = performance.now();
      const stop = () => this.cancelScroll();
      this._stopper = stop;
      ['wheel', 'touchstart', 'keydown'].forEach((ev) => addEventListener(ev, stop, { passive: true, once: true }));
      const html = document.documentElement, prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      const step = (now) => {
        const t = clamp((now - t0) / D, 0, 1);
        scrollTo(0, from + dist * honey(t));
        if (t < 1) this.scrollRaf = requestAnimationFrame(step);
        else { this.scrollRaf = 0; html.style.scrollBehavior = prev; this.cleanStopper(); done && done(); }
      };
      this._scrollDone = () => { html.style.scrollBehavior = prev; done && done(); };
      this.scrollRaf = requestAnimationFrame(step);
    }
    cleanStopper() {
      if (!this._stopper) return;
      ['wheel', 'touchstart', 'keydown'].forEach((ev) => removeEventListener(ev, this._stopper));
      this._stopper = null;
    }
    cancelScroll() {
      if (this.scrollRaf) { cancelAnimationFrame(this.scrollRaf); this.scrollRaf = 0; this._scrollDone && this._scrollDone(); }
      this.cleanStopper();
    }

    // Ziel so platzieren, dass weder Header noch Themen-Leiste es verdecken
    // Layout-Position (ohne transforms – Einblend-Animationen verschieben sonst)
    absTop(el) {
      let y = 0;
      for (let n = el; n; n = n.offsetParent) y += n.offsetTop;
      return y;
    }

    scrollToEl(el, instant, extra = 24, done) {
      const abs = this.absTop(el);
      const goingDown = abs - this.chipsH() - extra > scrollY;
      const offset = (goingDown ? 0 : this.headerH()) + this.chipsH() + extra;
      this.scrollToY(abs - offset, instant, done);
    }

    goTopic(id, instant) {
      const sec = this.sections.find((s) => s.dataset.fqSection === id);
      if (!sec) return;
      this.lockSpy = true;
      this.setActive(id);
      this.scrollToEl(sec, instant, 24, () => {
        setTimeout(() => { this.lockSpy = false; this.spy(); }, 120);
      });
      history.replaceState(history.state, '', `#${id}`);
    }

    goItem(item, instant) {
      this.open(item, true);
      this.scrollToEl(item, instant, 18, () => this.glow(item));
    }

    glow(el) {
      if (REDUCED.matches) return;
      el.classList.remove('is-glow');
      void el.offsetWidth;
      el.classList.add('is-glow');
      clearTimeout(el._glowT);
      el._glowT = setTimeout(() => el.classList.remove('is-glow'), 2800);
    }

    fromHash(initial) {
      const id = decodeURIComponent(location.hash.slice(1));
      if (!id) return;
      const target = document.getElementById(id);
      if (!target || !this.contains(target)) return;
      if (target.classList.contains('fq-item')) this.goItem(target, initial);
      else if (target.dataset.fqSection) this.goTopic(id, initial);
      else { this.scrollToEl(target, initial, 24, () => this.glow(target)); }
    }

    /* ============================================================
       Klicks + Tastatur
       ============================================================ */
    onClick(e) {
      const trig = e.target.closest('.fq-trigger');
      if (trig) { this.toggle(trig.closest('.fq-item')); return; }

      const copy = e.target.closest('[data-fq-copy]');
      if (copy) { this.copy(copy); return; }

      const a = e.target.closest('a[href*="#"]');
      if (!a) return;
      const url = new URL(a.href, location.href);
      if (url.pathname !== location.pathname || !url.hash) return;
      const id = decodeURIComponent(url.hash.slice(1));
      const target = document.getElementById(id);
      if (!target || !this.contains(target)) return;
      e.preventDefault();
      if (a.dataset.fqTopic) { this.goTopic(id); return; }
      if (target.classList.contains('fq-item')) { history.replaceState(history.state, '', `#${id}`); this.goItem(target); return; }
      this.scrollToEl(target, false, 24, () => this.glow(target));
    }

    onKey(e) {
      const trig = e.target.closest && e.target.closest('.fq-trigger');
      if (!trig) return;
      const all = this.items.map((i) => i._btn);
      const i = all.indexOf(trig);
      let next = null;
      if (e.key === 'ArrowDown') next = all[Math.min(all.length - 1, i + 1)];
      else if (e.key === 'ArrowUp') next = all[Math.max(0, i - 1)];
      else if (e.key === 'Home') next = all[0];
      else if (e.key === 'End') next = all[all.length - 1];
      if (next) { e.preventDefault(); next.focus(); }
    }

    async copy(btn) {
      const url = `${location.origin}${location.pathname}#${btn.dataset.fqCopy}`;
      let ok = false;
      try { await navigator.clipboard.writeText(url); ok = true; } catch (err) {
        const ta = document.createElement('textarea');
        ta.value = url; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { ok = document.execCommand('copy'); } catch (e2) { ok = false; }
        ta.remove();
      }
      if (ok) {
        btn.classList.add('is-done');
        clearTimeout(btn._t);
        btn._t = setTimeout(() => btn.classList.remove('is-done'), 2200);
      } else this.say('Link: ' + url);
    }

    say(msg) {
      if (!this.toast) return;
      this.toast.textContent = msg;
      this.toast.classList.add('is-on');
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => this.toast.classList.remove('is-on'), 3200);
    }
  }

  customElements.define('regina-faq', ReginaFaq);
})();
